import type { DynamicFieldComponentProps, DynamicFormButtonSetting } from '@myosh/odin-components';
import React, { useEffect, useRef, useState } from 'react';
import { Observable, skipWhile, Subject, take, takeUntil } from 'rxjs';
import type { RecordResult } from '../../../@types/records';
import { forceAssert, isEmptyObject } from '../../../utils/type-guards';
import WrapperStyledButtonField from '../../common/wrapper-styled-button-field';
import InviteDialog from './invite-modal.component';

export interface InviteButtonFieldProps extends DynamicFieldComponentProps<unknown> {
  buttonCaption?: string;
  recordId?: number;
  saveRecord?: () => Observable<RecordResult>;
  updateLastChangeValue?: (lastChange: string) => void;
  setFormButtonsState?: React.Dispatch<React.SetStateAction<DynamicFormButtonSetting[]>>;
}

function InviteButtonField({
  id,
  buttonCaption,
  recordId,
  readOnly,
  saveRecord,
  updateLastChangeValue,
  setFormButtonsState,
}: InviteButtonFieldProps) {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const destroySubject = useRef(new Subject<void>());

  useEffect(() => {
    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  const onOpenDialog = () => {
    if (recordId === undefined) {
      const resultObservable = saveRecord?.();
      if (resultObservable) {
        resultObservable
          .pipe(
            skipWhile((result) => isEmptyObject(forceAssert<Record<string, unknown>>(result))),
            take(1),
            takeUntil(destroySubject.current)
          )
          .subscribe(() => {
            setShowInviteDialog(true);
          });
      }
    } else {
      setShowInviteDialog(true);
    }
  };
  const onCloseDialog = () => setShowInviteDialog(false);

  return (
    <>
      <WrapperStyledButtonField onClick={onOpenDialog} hidden={readOnly} buttonCaption={buttonCaption} />
      <InviteDialog
        fieldId={id}
        recordId={recordId ?? 0}
        visible={showInviteDialog}
        hide={onCloseDialog}
        updateLastChangeValue={updateLastChangeValue}
        setFormButtonsState={setFormButtonsState}
        readOnly={readOnly}
      />
    </>
  );
}

export default InviteButtonField;
